import React, {
  useMemo,
  useState
} from "react";
import styled, { css } from "styled-components";
import { useCombobox } from "downshift";

import { useSearchApi } from "src/core/shared/api/search.api";
import {
  FilterParams,
  ITerminology
} from "src/library/utils";
import { Text } from "src/library/components/Text";
import { Icon } from "src/library/components/Icon";
import { Icons } from "src/library/components/Icon/types";
import { LoadingSpinner } from "src/library/components/LoadingSpinner";

import { IUser } from "../api/users.api";
import { UserSearchComponent } from "./UserSearchComponent";

// force consumer to hold user state
export interface SelectedUserProps {
  user: IUser | null;
  // for updating local state
  updateUser?: React.Dispatch<React.SetStateAction<IUser | null>>;
  // for running custom logic like an api req
  handleUpdate?: (data: IUser | null) => void;
  terminology?: ITerminology;
  userGroupId?: number;
  disabled?: boolean;
}

const SelectedUser: React.ReactFCWithChildren<SelectedUserProps> = ({
  user, updateUser, handleUpdate, terminology, userGroupId, disabled = false
}) => {
  const [ listSearchResponse, getSearch ] = useSearchApi("SEARCH_USERS");
  const [ showSearch, setShowSearch ] = useState(false);
  const [ isLoading, setIsLoading ] = React.useState(false);

  const userFormated = useMemo(() => {
    return user ? `${user.firstName} ${user.lastName}` : " - ";
  }, [ user ]);

  React.useEffect(() => {
    if (userGroupId || userGroupId === null) {
      // preload options when userGroup changes
      getSearch({
        pagination: {
          offset: 0,
          limit: 100,
          page: 0,
          sort: [ [ "firstName", "DESC" ] ]
        },
        filters: {
          userGroupId,
          defaultCustomerId: null
        }
      });
    }
  }, [ getSearch, userGroupId ]);

  // run update on relevant provided callback
  const runUpdate = React.useCallback((data: IUser | null) => {
    if (updateUser) updateUser(data);

    if (handleUpdate) handleUpdate(data);
  }, [ handleUpdate, updateUser ]);

  // handle selecting a user
  const userDownShiftProps = useCombobox<IUser>({
    items: listSearchResponse.data ? listSearchResponse.data.result : [],
    itemToString: () => "",
    onSelectedItemChange: changes => {
      if (changes.selectedItem) {
        // anticipate api action
        if (handleUpdate) {
          setIsLoading(true);
        }

        runUpdate(changes.selectedItem);
        setShowSearch(false);

        return;
      }
    }
  });

  // update local loading state
  React.useEffect(() => {
    if (user || user === null) {
      setIsLoading(false);
    }
  }, [ user ]);

  if (showSearch) {
    return (
      <UserSearchComponent
        error={listSearchResponse.error}
        onSearch={(data: string) => {
          const filterData: FilterParams<IUser> & { userGroupId?: number; } = {
            defaultCustomerId: null,
            $or: [
              { email: { $like: `%${data}%` } },
              { firstName: { $like: `%${data}%` } },
              { lastName: { $like: `%${data}%` } }
            ]
          };

          if (userGroupId) {
            filterData[ "userGroupId" ] = userGroupId;
          }

          getSearch({
            pagination: {
              offset: 0,
              limit: 100,
              page: 0,
              sort: [ [ "firstName", "DESC" ] ]
            },
            filters: filterData
          });
        }}
        loading={listSearchResponse.pending}
        searchResults={listSearchResponse?.data?.result || []}
        downshiftProps={userDownShiftProps}
        placeholder={`Search ${terminology?.user.plural.text || "users"}...`}
        onClose={() => setShowSearch(false)}
        autoFocus={showSearch}
      />
    );
  }

  return (
    <SelectedUserWrapper
      hasUser={!!user}
      disabled={disabled}
    >
      {!isLoading && (
        <div
          className="selected-user"
          onClick={() => {
            if (!disabled) {
              setShowSearch(!showSearch);
            }
          }}
        >
          {!user && (
            <Icon
              className="user-icon"
              icon={Icons.PEOPLE}
              $fillColor="lightGrey"
            />
          )}

          <Text>
            {userFormated}
          </Text>
        </div>
      )}

      {isLoading && (
        <div className="loading-container">
          <LoadingSpinner
            type="flat"
            position="inline"
          />
        </div>
      )}

      {user && !disabled && (
        <div className="remove-user">
          <Icon
            icon={Icons.CLOSE}
            $fillColor="primaryDark"
            onClick={() => {
              // anticipate api action
              if (handleUpdate) {
                setIsLoading(true);
              }

              runUpdate(null);
            }}
          />
        </div>
      )}
    </SelectedUserWrapper>
  );
};

const SelectedUserWrapper = styled.div<{hasUser: boolean, disabled: boolean}>`
  ${({
    theme, hasUser, disabled
  }) => css`
    max-width: 400px;
    display: flex;

    .loading-container {
      min-width: 160px;
      display:flex;
      align-items: center;
    }

    .selected-user {
      color: black;
      background-color: ${theme.colors.white};
      border: 2px solid ${theme.colors.lightGrey};
      height: 40px;
      border-radius: ${theme.borderRadius};
      display: flex;
      align-items: center;
      padding: ${theme.spacing.padding.sm} ${theme.spacing.padding.md};
      justify-content: ${hasUser ? "space-between" : "space-start"};
      font-weight: bold;
      width: 100%;
  
      &:hover {
        border-color: ${theme.colors.midGrey};
        cursor: ${disabled ? "not-allowed" : "pointer"};
      }

    }
    
    .user-icon {
      margin-right: ${theme.spacing.margins.md};
    }

    .remove-user {
      margin-left: ${theme.spacing.margins.sm};
      display: flex;
      justify-content: center;
      align-items: center;
      cursor: pointer;
    }
  `}
`;

export default SelectedUser;
